import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Features, ProductTypes, RoutePaths } from '../../Constants/Common';
import ModalDialog from '../common/ModalDialog';
import ProgressButton from '../common/ProgressButton';
import { VehicleState } from 'state/types/vehicle';
import Policies from './Policies';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/hooks';
import { selectIsEUSite, selectIsUSSite } from 'state/selectors/site';

const PolicySection = (): JSX.Element => {
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const selectedPolicy = useAppSelector((s) => s.vehicles.selectedPolicy);
    if (!vehicles || vehicles.length === 0) {
        return <></>;
    }

    const { t } = useTranslation();

    const isUsSite = useAppSelector(selectIsUSSite);
    const isEUSite = useAppSelector(selectIsEUSite);

    const [showDialog, setShowDialog] = useState(false);

    const [showModalForClaim, setShowModalForClaim] = useState(false);

    const navigate = useNavigate();

    const features = useAppSelector((s) => s.site.features);

    const navigateToRepairRequestPage = (
        productRoute: string,
        event?: React.FormEvent
    ) => {
        const showPreCheckPage =
            productRoute === RoutePaths.Tyre ||
            (isUsSite && selectedPolicy.type !== ProductTypes.GAP);

        if (!showPreCheckPage) {
            navigate(productRoute);
            return;
        }

        event?.preventDefault(); // do we need it?
        navigate(RoutePaths.RepairRequestPreCheckPage, {
            state: {
                productRoute
            }
        });
    };

    const onRequestRepairClicked = (event: React.FormEvent) => {
        if (!selectedPolicy) {
            event.preventDefault();
            return;
        }

        const isJointProduct =
            selectedPolicy.type === ProductTypes.JointAlloyCosmetic ||
            selectedPolicy.type === ProductTypes.JointAlloyTyre;

        if (isJointProduct) {
            event.preventDefault();
            setShowDialog(true);
            return;
        }

        navigateToRepairRequestPage(getProductRoute(selectedPolicy), event);
    };

    const onPriorRepairRequestClicked = (event: React.FormEvent) => {
        if (!selectedPolicy) {
            event.preventDefault();
        }
    };

    const onViewDocumentsClicked = (event: React.FormEvent) => {
        if (!selectedPolicy) {
            event.preventDefault();
        }
        navigate(RoutePaths.ViewDocuments);
    };

    const onOpenNewClaimClicked = (event: React.FormEvent) => {
        event.preventDefault();

        if (features.GapClaim === Features.GapClaim.Off || !isUsSite) {
            setShowModalForClaim(true);
            setShowDialog(true);
        } else {
            navigate(RoutePaths.ClaimDetails);
        }
    };

    const onCloseModalClick = () => {
        setShowDialog(false);
        setShowModalForClaim(false);
    };

    const onJointAlloyConfirmation = () => {
        navigateToRepairRequestPage(RoutePaths.Alloy);
    };

    const onJointCosmeticConfirmation = () => {
        navigateToRepairRequestPage(RoutePaths.Cosmetic);
    };

    const onJointTyreConfirmation = () => {
        navigateToRepairRequestPage(RoutePaths.Tyre);
    };

    const sharedrequestButtonStyle = 'btn btn-primary progress-button';
    const policy = selectedPolicy;
    const requestButtonStyle =
        policy && policy.status === 'Active'
            ? `${sharedrequestButtonStyle}`
            : `${sharedrequestButtonStyle} disabled`;

    const requestButtonDocumentStyle = policy
        ? `${sharedrequestButtonStyle}`
        : `${sharedrequestButtonStyle} disabled`;

    const getProductRoute = (selectedProduct) => {
        switch (selectedProduct?.type) {
            case ProductTypes.Alloy:
            case ProductTypes.AlloyRepair:
                return RoutePaths.Alloy;
            case ProductTypes.Cosmetic:
            case ProductTypes.CosmeticRepair:
                return RoutePaths.Cosmetic;
            case ProductTypes.JointAlloyCosmetic:
            case ProductTypes.JointAlloyTyre:
                return RoutePaths.ToResolveFromProductType;
            case ProductTypes.Tyre:
                return RoutePaths.Tyre;
            default:
                return undefined;
        }
    };
    const selectedProduct = selectedPolicy ?? null;

    const isEuropeUserWithoutGapProduct =
        isEUSite &&
        selectedProduct &&
        selectedProduct.type !== ProductTypes.GAP;

    // UKTyreClaim feature flag
    const isUKTyreClaimFeatureOff =
        features.UKTyreClaim === Features.UKTyreClaim.Off && !isUsSite;
    const isTyreClaimsUnavailable =
        isUKTyreClaimFeatureOff && policy?.type === ProductTypes.Tyre;
    const isTyreButtonDisabled =
        isUKTyreClaimFeatureOff && policy?.type === ProductTypes.JointAlloyTyre;

    const createProductNavigation = () => {
        if (isTyreClaimsUnavailable) {
            return (
                <p className='error'>{t('option_not_available_for_product')}</p>
            );
        }

        if (isEuropeUserWithoutGapProduct) {
            return <></>;
        } else {
            if (
                selectedProduct &&
                getProductRoute(selectedProduct) === undefined
            ) {
                if (selectedProduct.type === ProductTypes.GAP) {
                    return (
                        <ProgressButton
                            onClick={onOpenNewClaimClicked}
                            navRoute=''
                            buttonStyle={requestButtonStyle}
                            text={t('open_a_claim')}
                        />
                    );
                } else {
                    return (
                        <p className='error'>
                            {selectedProduct.type === ProductTypes.Warranty
                                ? t('option_not_available_for_product')
                                : t('cannot_request_a_repair_for_this_product')}
                        </p>
                    );
                }
            }
        }

        const productRoute = getProductRoute(selectedProduct) || '/';

        return (
            <ProgressButton
                onClick={onRequestRepairClicked}
                navRoute={productRoute}
                locationState={{ product: selectedProduct }}
                buttonStyle={requestButtonStyle}
                text={t('request_a_repair')}
                dataTestid='request_a_repair-button'
            />
        );
    };
    const createPriorRepairRequestButton = () => {
        if (isEuropeUserWithoutGapProduct) {
            return <div></div>;
        }
        return (
            <ProgressButton
                onClick={onPriorRepairRequestClicked}
                navRoute={RoutePaths.ViewRequests}
                buttonStyle={requestButtonStyle}
                text={
                    selectedProduct?.type === ProductTypes.GAP
                        ? t('prior_claim_requests')
                        : t('prior_repair_requests')
                }
            />
        );
    };

    const navButton = createProductNavigation();
    const renderPriorRepairRequestButton = createPriorRepairRequestButton();

    const renderPolicies = (vehicle: VehicleState, i: number) => {
        return (
            <div id={`vehicle-${i.toString()}`}>
                <div className='card-header'>
                    <p className='card-title'>{vehicle?.vrn}</p>
                    <p className='card-title'>{vehicle?.vin}</p>
                    <span className='text-muted'>{`${vehicle?.make} ${vehicle?.model}`}</span>
                </div>
                {vehicle.policies && (
                    <Policies vehicleIndex={i} policies={vehicle.policies} />
                )}
            </div>
        );
    };

    const renderJointProductDialog = () => {
        return (
            <>
                <button
                    className='btn btn-primary'
                    onClick={onJointAlloyConfirmation}>
                    {t('alloy_wheel')}
                </button>
                {policy && policy.type === ProductTypes.JointAlloyCosmetic ? (
                    <button
                        className='btn btn-primary'
                        onClick={onJointCosmeticConfirmation}>
                        {t('cosmetic')}
                    </button>
                ) : (
                    <button
                        disabled={isTyreButtonDisabled}
                        className='btn btn-primary'
                        onClick={
                            !isTyreButtonDisabled && onJointTyreConfirmation
                        }>
                        {t('tyre')}
                    </button>
                )}
            </>
        );
    };

    const modalTitle =
        policy && policy.type === ProductTypes.JointAlloyCosmetic
            ? t('modal_title_cosmetic')
            : t('modal_title_tyre');

    return (
        <>
            <div className='card formInput--card'>
                <div className='card-header-container'>
                    <div className='card-header header-left'>
                        {t('vehicle_policies')}
                    </div>
                </div>
                {vehicles.map((vehicle, index) => (
                    <div key={`vehicle-${index.toString()}`}>
                        {renderPolicies(vehicle, index)}
                    </div>
                ))}
                <ModalDialog
                    showDialog={showDialog}
                    hideFooter={true}
                    onCloseClick={onCloseModalClick}>
                    <p>
                        {showModalForClaim
                            ? features.GapClaim === Features.GapClaim.Off
                                ? t('help_text.feature_not_available')
                                : t('help_text.open_claim_message')
                            : modalTitle}
                    </p>
                    <div
                        className={
                            showModalForClaim
                                ? 'open-claim-modal-container'
                                : 'joint-alloy-cosmetic-container'
                        }>
                        {showModalForClaim ? (
                            <button
                                className='btn btn-primary'
                                onClick={onCloseModalClick}>
                                {t('button_label.close')}
                            </button>
                        ) : (
                            <>{renderJointProductDialog()}</>
                        )}
                    </div>
                </ModalDialog>
            </div>
            <div className='bottom-navigation'>
                {navButton}
                {renderPriorRepairRequestButton}
                <ProgressButton
                    onClick={onViewDocumentsClicked}
                    navRoute={RoutePaths.ViewDocuments}
                    buttonStyle={requestButtonDocumentStyle}
                    text={t('view_documents')}
                />
            </div>
        </>
    );
};

export default PolicySection;
